import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

const SphereNetwork: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const animationIdRef = useRef<number | null>(null);

  // 数据传输节点接口
  interface DataNode {
    mesh: THREE.Mesh;
    startParticle: number;
    endParticle: number;
    progress: number;
    speed: number;
  }

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    let group: THREE.Group;
    const particlesData: Array<{
      originalPosition: THREE.Vector3;
      currentAngle: number;
      animationRadius: number;
      numConnections: number;
    }> = [];
    let camera: THREE.PerspectiveCamera;
    let scene: THREE.Scene;
    let renderer: THREE.WebGLRenderer;
    let positions: Float32Array;
    let colors: Float32Array;
    let particles: THREE.BufferGeometry;
    let pointCloud: THREE.Points;
    let particlePositions: Float32Array;
    let linesMesh: THREE.LineSegments;
    const dataNodes: DataNode[] = [];
    let activeConnections: Array<{ start: number; end: number }> = [];

    // 根据屏幕大小调整参数
    const isMobile = window.innerWidth < 768;
    
    const maxParticleCount = 80;
    const particleCount = 40;
    const sphereRadius = isMobile ? 230 : 300;

    const effectController = {
      showDots: true,
      showLines: true,
      minDistance: 1050,
      limitConnections: false,
      maxConnections: 25,
      animationSpeed: 0.005,
      rotationSpeed: 0.02,
    };

    function fibonacciSphere(samples: number, radius: number) {
      const points: Array<{ x: number; y: number; z: number }> = [];
      const phi = Math.PI * (3.0 - Math.sqrt(5.0));

      for (let i = 0; i < samples; i++) {
        const y = 1 - (i / (samples - 1)) * 2;
        const radiusAtY = Math.sqrt(1 - y * y);
        const theta = phi * i;
        const x = Math.cos(theta) * radiusAtY;
        const z = Math.sin(theta) * radiusAtY;

        points.push({
          x: x * radius,
          y: y * radius,
          z: z * radius,
        });
      }
      return points;
    }

    function init() {
      // 根据屏幕大小调整相机参数（移动端增加距离使球体看起来更小）
      const cameraDistance = isMobile ? 1100 : 950;
      
      camera = new THREE.PerspectiveCamera(
        isMobile ? 55 : 45,
        window.innerWidth / window.innerHeight,
        1,
        4000
      );
      camera.position.z = cameraDistance;

      const controls = new OrbitControls(camera, container);
      controls.minDistance = isMobile ? 300 : 200;
      controls.maxDistance = cameraDistance;
      // 禁止上下翻转，锁定极角（垂直旋转）
      controls.minPolarAngle = Math.PI / 2;
      controls.maxPolarAngle = Math.PI / 2;
      // 禁止左右反转
      controls.minAzimuthAngle = 0;
      controls.maxAzimuthAngle = 0;
      // 禁用缩放功能
      controls.enableZoom = false;
      // 移动端禁用所有控制
      if (isMobile) {
        controls.enabled = false;
      }

      scene = new THREE.Scene();

      group = new THREE.Group();
      // 默认向上翻转 90 度
      group.rotation.x = Math.PI / 2;
     // group.rotation.y = -Math.PI / 2;
      scene.add(group);

      const segments = maxParticleCount * maxParticleCount;

      positions = new Float32Array(segments * 3);
      colors = new Float32Array(segments * 3);

      // Create circular texture for round dots
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d')!;
      const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
      gradient.addColorStop(0, 'rgba(215, 254, 81, 1)');
      gradient.addColorStop(0.5, 'rgba(215, 254, 81, 0.5)');
      gradient.addColorStop(1, 'rgba(215, 254, 81, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 32, 32);
      const circleTexture = new THREE.CanvasTexture(canvas);

      // 根据屏幕大小调整点的大小
      const pointSize = isMobile ? 10 : 14;
      
      const pMaterial = new THREE.PointsMaterial({
        color: 0xd7fe51,
        size: pointSize,
        map: circleTexture,
        blending: THREE.AdditiveBlending,
        transparent: true,
        sizeAttenuation: false,
        depthWrite: false,
      });

      particles = new THREE.BufferGeometry();
      particlePositions = new Float32Array(maxParticleCount * 3);

      const points = fibonacciSphere(maxParticleCount, sphereRadius);

      for (let i = 0; i < maxParticleCount; i++) {
        const point = points[i] || { x: 0, y: 0, z: 0 };

        particlePositions[i * 3] = point.x;
        particlePositions[i * 3 + 1] = point.y;
        particlePositions[i * 3 + 2] = point.z;

        particlesData.push({
          originalPosition: new THREE.Vector3(point.x, point.y, point.z),
          currentAngle: Math.random() * Math.PI * 2,
          animationRadius: 5 + Math.random() * 20,
          numConnections: 0,
        });
      }

      particles.setDrawRange(0, particleCount);
      particles.setAttribute(
        'position',
        new THREE.BufferAttribute(particlePositions, 3).setUsage(
          THREE.DynamicDrawUsage
        )
      );

      pointCloud = new THREE.Points(particles, pMaterial);
      group.add(pointCloud);

      const geometry = new THREE.BufferGeometry();

      geometry.setAttribute(
        'position',
        new THREE.BufferAttribute(positions, 3).setUsage(THREE.DynamicDrawUsage)
      );
      geometry.setAttribute(
        'color',
        new THREE.BufferAttribute(colors, 3).setUsage(THREE.DynamicDrawUsage)
      );

      geometry.computeBoundingSphere();
      geometry.setDrawRange(0, 0);

      const material = new THREE.LineBasicMaterial({
        vertexColors: true,
        color: 0xd7fe51,
        blending: THREE.NormalBlending,
        transparent: true,
        opacity: 0.6,
      });

      linesMesh = new THREE.LineSegments(geometry, material);
      group.add(linesMesh);

      // 根据屏幕大小调整数据节点大小
      const dataNodeSize = isMobile ? 2 : 2.5;
      const dataNodeCount = 15;
      
      const dataNodeGeometry = new THREE.SphereGeometry(dataNodeSize, 16, 16);
      const dataNodeMaterial = new THREE.MeshBasicMaterial({ 
        color: 0x9ca3af, // gray-400
        transparent: true,
        opacity: 0.8
      });

      for (let i = 0; i < dataNodeCount; i++) {
        const dataNodeMesh = new THREE.Mesh(dataNodeGeometry, dataNodeMaterial);
        group.add(dataNodeMesh);
        
        // 随机选择起始和结束粒子
        const startParticle = Math.floor(Math.random() * particleCount);
        let endParticle = Math.floor(Math.random() * particleCount);
        while (endParticle === startParticle) {
          endParticle = Math.floor(Math.random() * particleCount);
        }
        
        dataNodes.push({
          mesh: dataNodeMesh,
          startParticle,
          endParticle,
          progress: Math.random(), // 随机初始进度
          speed: 0.003 + Math.random() * 0.005 // 随机速度
        });
      }

      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(window.innerWidth, window.innerHeight);
      rendererRef.current = renderer;
      container.appendChild(renderer.domElement);

      window.addEventListener('resize', onWindowResize);
    }

    function onWindowResize() {
      const isMobile = window.innerWidth < 768;
      const cameraDistance = isMobile ? 1100 : 950;
      
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.fov = isMobile ? 55 : 45;
      camera.position.z = cameraDistance;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    }

    function animate() {
      animationIdRef.current = requestAnimationFrame(animate);

      let vertexpos = 0;
      let colorpos = 0;
      let numConnected = 0;
      activeConnections = [];

      for (let i = 0; i < particleCount; i++)
        particlesData[i].numConnections = 0;

      for (let i = 0; i < particleCount; i++) {
        const particleData = particlesData[i];

        particleData.currentAngle += effectController.animationSpeed;

        const offset = new THREE.Vector3(
          Math.sin(particleData.currentAngle) * particleData.animationRadius,
          Math.cos(particleData.currentAngle * 1.3) * particleData.animationRadius,
          Math.sin(particleData.currentAngle * 0.7) * particleData.animationRadius
        );

        const newPosition = particleData.originalPosition.clone().add(offset);

        particlePositions[i * 3] = newPosition.x;
        particlePositions[i * 3 + 1] = newPosition.y;
        particlePositions[i * 3 + 2] = newPosition.z;

        if (
          effectController.limitConnections &&
          particleData.numConnections >= effectController.maxConnections
        )
          continue;

        for (let j = i + 1; j < particleCount; j++) {
          const particleDataB = particlesData[j];
          if (
            effectController.limitConnections &&
            particleDataB.numConnections >= effectController.maxConnections
          )
            continue;

          const dx = particlePositions[i * 3] - particlePositions[j * 3];
          const dy = particlePositions[i * 3 + 1] - particlePositions[j * 3 + 1];
          const dz = particlePositions[i * 3 + 2] - particlePositions[j * 3 + 2];
          const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

          if (dist < effectController.minDistance) {
            particleData.numConnections++;
            particleDataB.numConnections++;

            const alpha = 1.0 - dist / effectController.minDistance;

            positions[vertexpos++] = particlePositions[i * 3];
            positions[vertexpos++] = particlePositions[i * 3 + 1];
            positions[vertexpos++] = particlePositions[i * 3 + 2];

            positions[vertexpos++] = particlePositions[j * 3];
            positions[vertexpos++] = particlePositions[j * 3 + 1];
            positions[vertexpos++] = particlePositions[j * 3 + 2];

            colors[colorpos++] = 0.843 * alpha;
            colors[colorpos++] = 0.996 * alpha;
            colors[colorpos++] = 0.318 * alpha;

            colors[colorpos++] = 0.843 * alpha;
            colors[colorpos++] = 0.996 * alpha;
            colors[colorpos++] = 0.318 * alpha;

            numConnected++;
            
            // 记录活跃的连接
            activeConnections.push({ start: i, end: j });
          }
        }
      }

      // 更新数据传输节点位置
      dataNodes.forEach(dataNode => {
        // 更新进度
        dataNode.progress += dataNode.speed;
        
        // 如果到达终点，随机选择新的路径
        if (dataNode.progress >= 1) {
          dataNode.progress = 0;
          
          // 从活跃连接中随机选择一条
          if (activeConnections.length > 0) {
            const randomConnection = activeConnections[Math.floor(Math.random() * activeConnections.length)];
            dataNode.startParticle = randomConnection.start;
            dataNode.endParticle = randomConnection.end;
          } else {
            // 如果没有活跃连接，随机选择两个粒子
            dataNode.startParticle = Math.floor(Math.random() * particleCount);
            dataNode.endParticle = Math.floor(Math.random() * particleCount);
          }
        }
        
        // 计算当前位置（线性插值）
        const startIdx = dataNode.startParticle * 3;
        const endIdx = dataNode.endParticle * 3;
        
        const startX = particlePositions[startIdx];
        const startY = particlePositions[startIdx + 1];
        const startZ = particlePositions[startIdx + 2];
        
        const endX = particlePositions[endIdx];
        const endY = particlePositions[endIdx + 1];
        const endZ = particlePositions[endIdx + 2];
        
        // 使用缓动函数使运动更平滑
        const easeProgress = dataNode.progress < 0.5 
          ? 2 * dataNode.progress * dataNode.progress 
          : 1 - Math.pow(-2 * dataNode.progress + 2, 2) / 2;
        
        dataNode.mesh.position.x = startX + (endX - startX) * easeProgress;
        dataNode.mesh.position.y = startY + (endY - startY) * easeProgress;
        dataNode.mesh.position.z = startZ + (endZ - startZ) * easeProgress;
      });

      linesMesh.geometry.setDrawRange(0, numConnected * 2);
      linesMesh.geometry.attributes.position.needsUpdate = true;
      linesMesh.geometry.attributes.color.needsUpdate = true;

      pointCloud.geometry.attributes.position.needsUpdate = true;

      render();
    }

    function render() {
      const time = Date.now() * 0.001;
      group.rotation.y = time * effectController.rotationSpeed;
      renderer.render(scene, camera);
    }

    init();
    animate();

    return () => {
      window.removeEventListener('resize', onWindowResize);
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      if (rendererRef.current) {
        rendererRef.current.dispose();
        if (container?.contains(rendererRef.current.domElement)) {
          container.removeChild(rendererRef.current.domElement);
        }
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 w-full h-full z-0"
      style={{ background: 'transparent', pointerEvents: 'none' }}
    />
  );
};

export default SphereNetwork;
